if (!customElements.get('cart-upsells')) {
  customElements.define(
    'cart-upsells',
    class CartUpsells extends HTMLElement {
      constructor() {
        super();
        this.productsContainer = null;
        this.loadingEl = null;
        this.maxProducts = 4;
      }

      connectedCallback() {
        this.productsContainer = this.querySelector('.cart-upsells__products');
        this.loadingEl = this.querySelector('.cart-upsells__loading');
        this.fetchUpsells();
      }

      get productIds() {
        const raw = this.dataset.productIds || '';
        return raw.split(',').map((id) => id.trim()).filter(Boolean);
      }

      get cartVariantIds() {
        const raw = this.dataset.cartVariantIds || '';
        return raw.split(',').map((id) => id.trim()).filter(Boolean);
      }

      get cartProductIds() {
        return new Set(this.productIds);
      }

      get recommendationsUrl() {
        return this.dataset.recommendationsUrl;
      }

      async fetchUpsells() {
        const productIds = this.productIds;
        if (!productIds.length) {
          this.hide();
          return;
        }

        this.showLoading();

        try {
          const fetches = productIds.map((id) =>
            fetch(`${this.recommendationsUrl}.json?product_id=${id}&limit=4&intent=complementary`)
              .then((res) => (res.ok ? res.json() : { products: [] }))
              .catch(() => ({ products: [] }))
          );

          const results = await Promise.all(fetches);
          const seen = new Set();
          const cartProductIdSet = this.cartProductIds;
          const upsellProducts = [];

          for (const result of results) {
            for (const product of result.products || []) {
              if (cartProductIdSet.has(String(product.id))) continue;
              if (seen.has(product.id)) continue;
              if (!product.available) continue;
              seen.add(product.id);
              upsellProducts.push(product);
              if (upsellProducts.length >= this.maxProducts) break;
            }
            if (upsellProducts.length >= this.maxProducts) break;
          }

          this.hideLoading();

          if (upsellProducts.length === 0) {
            this.hide();
            return;
          }

          this.renderProducts(upsellProducts);
          this.show();
        } catch (err) {
          console.error('Cart upsells fetch error:', err);
          this.hideLoading();
          this.hide();
        }
      }

      renderProducts(products) {
        this.productsContainer.innerHTML = products.map((product) => this.renderCard(product)).join('');

        this.productsContainer.querySelectorAll('.cart-upsell-card__add').forEach((btn) => {
          btn.addEventListener('click', this.handleAddToCart.bind(this));
        });
      }

      renderCard(product) {
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        const image = product.featured_image || product.images?.[0];
        const imageUrl = image ? this.getSizedImageUrl(image, '200x') : '';
        const price = this.formatMoney(variant.price);
        const comparePrice = variant.compare_at_price > variant.price
          ? this.formatMoney(variant.compare_at_price)
          : '';

        return `
          <div class="cart-upsell-card" data-product-id="${product.id}">
            <a href="${product.url}" class="cart-upsell-card__image-link">
              ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="200" height="250" class="cart-upsell-card__image">` : ''}
            </a>
            <div class="cart-upsell-card__info">
              <a href="${product.url}" class="cart-upsell-card__title unstyled-link">${this.escapeHtml(product.title)}</a>
              <div class="cart-upsell-card__price">
                ${comparePrice ? `<s class="cart-upsell-card__compare-price">${comparePrice}</s>` : ''}
                <span>${price}</span>
              </div>
            </div>
            <button
              type="button"
              class="cart-upsell-card__add"
              data-variant-id="${variant.id}"
              aria-label="Add ${this.escapeHtml(product.title)} to cart"
            >
              <span class="cart-upsell-card__add-text">Add</span>
              <span class="cart-upsell-card__add-loading hidden">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </span>
            </button>
          </div>
        `;
      }

      async handleAddToCart(evt) {
        const btn = evt.currentTarget;
        const variantId = btn.dataset.variantId;

        btn.setAttribute('disabled', 'true');
        btn.querySelector('.cart-upsell-card__add-text').classList.add('hidden');
        btn.querySelector('.cart-upsell-card__add-loading').classList.remove('hidden');

        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

        try {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({
              id: parseInt(variantId),
              quantity: 1,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });

          const data = await response.json();

          if (data.status) {
            console.error('Add to cart error:', data.description);
            btn.removeAttribute('disabled');
            btn.querySelector('.cart-upsell-card__add-text').classList.remove('hidden');
            btn.querySelector('.cart-upsell-card__add-loading').classList.add('hidden');
            return;
          }

          // Fetch cart state and dispatch cart:change event
          fetch(`${routes.cart_url}.js`)
            .then((res) => res.json())
            .then((cart) => {
              document.dispatchEvent(
                new CustomEvent('cart:change', {
                  detail: { cart, source: 'cart-upsells' },
                })
              );
            })
            .catch((err) => console.error('Error fetching cart state:', err));

          // Re-render cart drawer with updated sections
          if (cartDrawer) {
            cartDrawer.renderContents(data);
          }
        } catch (err) {
          console.error('Cart upsells add-to-cart error:', err);
          btn.removeAttribute('disabled');
          btn.querySelector('.cart-upsell-card__add-text').classList.remove('hidden');
          btn.querySelector('.cart-upsell-card__add-loading').classList.add('hidden');
        }
      }

      getSizedImageUrl(src, size) {
        if (typeof src === 'object') src = src.src || src.url || '';
        if (!src) return '';
        const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp|avif)(\?.*)?$/i);
        if (!match) return src;
        const prefix = src.split(match[0]);
        return `${prefix[0]}_${size}.${match[1]}${match[2] || ''}`;
      }

      formatMoney(cents) {
        if (typeof cents === 'string') cents = parseInt(cents);
        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }

      show() {
        this.style.display = '';
      }

      hide() {
        this.style.display = 'none';
      }

      showLoading() {
        if (this.loadingEl) this.loadingEl.style.display = '';
        if (this.productsContainer) this.productsContainer.innerHTML = '';
        this.style.display = '';
      }

      hideLoading() {
        if (this.loadingEl) this.loadingEl.style.display = 'none';
      }
    }
  );
}
